import React from "react";
import DocumentCard from "@features/vessel-nomination/nomination-details/components/DocumentCard/DocumentCard";
import { useDownloadNominationDocument } from "@features/vessel-nomination/nomination-details/hooks/useDownloadNominationDocument";
import type {
  DryBulkAttachment,
  DryBulkDecision,
  DryBulkDecisionAttachment,
} from "@services/dry-bulk-nomination/types";
import { convertUtcToUae } from "@utils/date-time";
import { useTranslation } from "react-i18next";
import { formatUaeDateTime } from "../utils/formatUaeDate";
import AttachmentFileLink from "./AttachmentFileLink";
import DryBulkSection from "./ui/DryBulkSection";

const LANG_BASE = "vessel-nomination.dry-bulk";
const SERVICE = "bff-vessel";

interface Props {
  testId: string;
  attachments: DryBulkAttachment[];
  decisions: DryBulkDecision[];
}

const uaeDay = (iso?: string | null) => (iso ? convertUtcToUae(iso) : undefined);

/** "Documents" tab of the nomination detail (VN-15, wireframe 11.1): one card per Shipping Agent attachment, then the files of each decision. */
const DryBulkDocumentsTab: React.FC<Props> = ({ testId, attachments, decisions }) => {
  const { t } = useTranslation();
  const { handlePreview, handleDownload } = useDownloadNominationDocument();
  const withFiles = decisions.filter((decision) => (decision.attachments ?? []).length > 0);

  return (
    <div className="flex flex-col gap-6" data-testid={testId}>
      {attachments.length === 0 ? (
        <span data-testid={`${testId}-empty`} className="text-sm">
          {t(`${LANG_BASE}.details.documents.empty`)}
        </span>
      ) : (
        <div
          className="grid grid-cols-1 gap-4 md:grid-cols-2"
          data-testid={`${testId}-attachments`}
        >
          {attachments.map((attachment, index) => (
            <DocumentCard
              key={attachment.fileUrl}
              testId={`${testId}-attachment-${index}`}
              documentName={attachment.documentName}
              documentType={attachment.documentType}
              otherSpecify={
                attachment.documentType === "OTHERS" ? attachment.otherSpecify : undefined
              }
              issueDate={uaeDay(attachment.issueDate)}
              expiryDate={uaeDay(attachment.expiryDate)}
              onPreview={() => handlePreview(SERVICE, attachment.fileUrl)}
              onDownload={() =>
                handleDownload(SERVICE, attachment.fileUrl, attachment.documentName ?? undefined)
              }
            />
          ))}
        </div>
      )}
      {withFiles.map((decision) => (
        <DryBulkSection
          key={decision.decisionKey ?? decision.decidedAt}
          testId={`${testId}-decision-files`}
          title={`${t(`${LANG_BASE}.review.decision.outcomes.${decision.decision}`)} – ${formatUaeDateTime(decision.decidedAt)}`}
        >
          <ul className="flex flex-col gap-1 text-sm" data-testid={`${testId}-decision-files-list`}>
            {(decision.attachments ?? []).map((file: DryBulkDecisionAttachment) => (
              <li key={file.fileUrl}>
                <AttachmentFileLink className="break-all" url={file.fileUrl} name={file.fileName} />
              </li>
            ))}
          </ul>
        </DryBulkSection>
      ))}
    </div>
  );
};

export default DryBulkDocumentsTab;
